/**
 * Single source of truth for fusion-canvas layout + framing.
 *
 * Engine: ELK `layered`, direction RIGHT, in a real browser Worker with a 4s
 * deadline and a deterministic Money Flow fallback. Layout runs ASYNCHRONOUSLY:
 * `layout.run()` returns immediately and node positions land later, when the
 * layout emits `layoutstop`. So:
 *   - every run uses `fit: false` (the adapter default `fit: true` would move the
 *     camera on its own after our framing logic);
 *   - `runLayout` / `runNamedLayout` return a Promise that settles on `layoutstop`;
 *   - `fitTo` called while a layout is in flight is DEFERRED until that layout
 *     stops, so "lay out, then frame once" works for every caller without each
 *     one having to await.
 *
 * Placement (CANVAS_PLACEMENT_LANGUAGE.md §3.3, R24): nodes declare a
 * `placement_role`; this module owns coordinates. When the laid-out set carries
 * at least two distinct non-context roles, ELK partitioning orders the columns
 * source → relay → subject/shared_relay → sink → context. Compound containers
 * take part via `elk.hierarchyHandling: INCLUDE_CHILDREN`, so edges that cross a
 * group boundary still drive layering.
 *
 * Context band: nodes with no flow role (`context`, the backend's `ghost`, or none declared) are not
 * given a far-right column. When the set is laid out by role (not by phase lane) they are kept out of
 * the ELK pass and placed afterwards in a grid band BELOW the flow, so the flow reads as a flow and
 * the wallets that merely touch it do not stretch it sideways.
 *
 * Phase lanes (Fusion macro view, fusionMacro.ts): when the set contains
 * `phase_lane` compound nodes, partitions follow each lane's `lane_order`
 * instead (every node takes the order of the lane it sits in; a top-level node
 * outside any lane goes after the last lane), so lanes run left to right.
 *
 * Both FusionCanvas and FusionToolbar route through here so there is never a
 * second, divergent relayout.
 */
import type cytoscape from 'cytoscape';
import { MONEY_FLOW_LAYOUT } from '../../utils/workerElkLayout';

/** Column order for the six placement roles (lower = further left). */
export const ROLE_PARTITION: Record<string, number> = {
  source: 0,
  relay: 1,
  subject: 2,
  shared_relay: 2,
  sink: 3,
  context: 4,
};
const CONTEXT_PARTITION = ROLE_PARTITION.context;

export const LAYOUT: any = {
  name: 'elk',
  animate: false,
  fit: false,
  nodeDimensionsIncludeLabels: true,
  padding: 40,
  elk: {
    algorithm: 'layered',
    'elk.direction': 'RIGHT',
    'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    // Deterministic picture per graph: same seed every visit, model order (the sorted input, see
    // sortForLayout) breaks ties, and a fixed aspect ratio so the container size at mount time
    // (cytoscape-elk otherwise passes width/height) cannot change the arrangement.
    'elk.randomSeed': '1',
    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
    aspectRatio: 1.6,
    // Spacing and crossing minimisation as the Workbench flow layout (components/GraphCanvas.tsx,
    // getLayoutConfig 'flow'): 110 between nodes of a layer, 140 between layers, layer-sweep crossings.
    // Orthogonal routing only shapes ELK's own edge clearances: cytoscape draws the edges itself.
    'elk.spacing.nodeNode': 110,
    'elk.layered.spacing.nodeNodeBetweenLayers': 140,
    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    'elk.layered.edgeRouting': 'ORTHOGONAL',
  },
};

/** Safety net: if a layout never reports `layoutstop` (engine error), release waiters. */
const LAYOUT_SETTLE_TIMEOUT_MS = 5000;

/** Per-instance in-flight layout, so fits can wait for positions to land. */
const inflight = new WeakMap<cytoscape.Core, Promise<void>>();

/** Named layout presets (spec §7). */
export type LayoutPreset = 'organic' | 'flow' | 'ring';
export const LAYOUT_PRESETS: { id: LayoutPreset; label: string }[] = [
  { id: 'organic', label: 'Organic' },
  { id: 'flow', label: 'Money Flow' },
  { id: 'ring', label: 'Ring' },
];
const PRESET_CFG: Record<LayoutPreset, any> = {
  organic: LAYOUT,
  flow: MONEY_FLOW_LAYOUT,
  ring: {
    name: 'concentric', animate: false, fit: false, padding: 40, minNodeSpacing: 44, equidistant: false,
    concentric: (n: any) => Number(n.data('size')) || 1, levelWidth: () => 2,
  },
};

/** Elements not hidden by step / module gating. */
export const visibleEles = (cy: cytoscape.Core) =>
  cy.elements().not('.cs-stephidden').not('.cs-modhidden');

/**
 * True when the set declares enough placement roles to lay out by column:
 * at least two distinct roles other than `context`.
 */
export function hasPlacementRoles(eles: cytoscape.Collection): boolean {
  const roles = new Set<string>();
  eles.nodes().forEach((n) => {
    const r = String(n.data('placement_role') || '');
    if (r && r !== 'context' && r in ROLE_PARTITION) roles.add(r);
  });
  return roles.size >= 2;
}

/** True when the set carries phase-lane compound nodes (macro view lanes). */
export function hasPhaseLanes(eles: cytoscape.Collection): boolean {
  return eles.nodes().some((n) => n.data('type') === 'phase_lane');
}

/** Padding inside lane / group compounds, leaving room for the header label. */
const COMPOUND_PADDING = '[top=34,left=16,bottom=16,right=16]';

/** ELK options partitioning by phase lane (see header). */
export function phaseLaneLayoutFor(eles: cytoscape.Collection): any {
  let maxOrder = -1;
  eles.nodes().forEach((n) => {
    if (n.data('type') === 'phase_lane') maxOrder = Math.max(maxOrder, Number(n.data('lane_order')) || 0);
  });
  const outside = maxOrder + 1;
  const laneOrderOf = (n: cytoscape.NodeSingular): number => {
    if (n.data('type') === 'phase_lane') return Number(n.data('lane_order')) || 0;
    const lane = n.ancestors('[type="phase_lane"]');
    return lane.length ? Number(lane.first().data('lane_order')) || 0 : outside;
  };
  return {
    ...LAYOUT,
    elk: { ...LAYOUT.elk, 'elk.partitioning.activate': true },
    nodeLayoutOptions: (n: cytoscape.NodeSingular) => {
      const opts: Record<string, string> = { 'elk.partitioning.partition': String(laneOrderOf(n)) };
      if (n.isParent()) opts['elk.padding'] = COMPOUND_PADDING;
      return opts;
    },
  };
}

/** ELK options for `eles`: phase lanes first, else role partitions when roles are present. */
export function elkLayoutFor(eles: cytoscape.Collection): any {
  if (hasPhaseLanes(eles)) return phaseLaneLayoutFor(eles);
  if (!hasPlacementRoles(eles)) return LAYOUT;
  return {
    ...LAYOUT,
    elk: { ...LAYOUT.elk, 'elk.partitioning.activate': true },
    nodeLayoutOptions: (n: cytoscape.NodeSingular) => {
      if (n.isParent()) return undefined;
      const role = String(n.data('placement_role') || '');
      const partition = role in ROLE_PARTITION ? ROLE_PARTITION[role] : CONTEXT_PARTITION;
      return { 'elk.partitioning.partition': String(partition) };
    },
  };
}

/**
 * Stable layout input order: nodes by placement-role column, then canonical id (falling back to
 * id); edges by source, target, id. The same graph gives the same order however the backend or
 * the store happened to list it, so ELK (which breaks ties by input order) places it identically.
 */
export function sortForLayout(eles: cytoscape.Collection): cytoscape.Collection {
  const cy = (eles as unknown as { cy(): cytoscape.Core }).cy();
  const idOf = (e: cytoscape.SingularElementArgument) => String(e.data('canonical_id') || e.id());
  const colOf = (n: cytoscape.NodeSingular) => {
    const role = String(n.data('placement_role') || '');
    return role in ROLE_PARTITION ? ROLE_PARTITION[role] : CONTEXT_PARTITION;
  };
  const cmp = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
  const nodes = eles.nodes().toArray().sort((a, b) =>
    (colOf(a) - colOf(b)) || cmp(idOf(a), idOf(b)) || cmp(a.id(), b.id()));
  const edges = eles.edges().toArray().sort((a, b) =>
    cmp(String(a.data('source')), String(b.data('source')))
    || cmp(String(a.data('target')), String(b.data('target'))) || cmp(a.id(), b.id()));
  return cy.collection([...nodes, ...edges] as unknown as cytoscape.CollectionArgument);
}

/** Gap between the bottom of the flow and the context band, and the band's grid cell. */
export const BAND_GAP = 180;
export const BAND_CELL_W = 130;
export const BAND_CELL_H = 100;

const FLOW_ROLES = new Set(['source', 'relay', 'subject', 'shared_relay', 'sink']);

/**
 * Top-level nodes that belong in the context band: no flow role (context, ghost, or undeclared).
 * Empty unless the set is laid out by role (two or more distinct flow roles, no phase lanes).
 * Children of a compound stay inside it.
 */
export function contextBandNodes(eles: cytoscape.Collection): cytoscape.NodeCollection {
  const none = eles.nodes().filter(() => false);
  if (hasPhaseLanes(eles) || !hasPlacementRoles(eles)) return none;
  return eles.nodes().filter((n) =>
    !n.isParent() && !n.data('parent') && !FLOW_ROLES.has(String(n.data('placement_role') || '')));
}

/** Grid the band nodes below the laid-out flow (deterministic order: canonical id, then id). */
export function placeContextBand(flow: cytoscape.Collection, band: cytoscape.NodeCollection): void {
  if (band.empty()) return;
  const flowNodes = flow.nodes();
  const bb = flowNodes.empty() ? { x1: 0, y2: 0, w: 0 } : flowNodes.boundingBox({});
  const sorted = band.toArray().sort((a, b) => {
    const ka = String(a.data('canonical_id') || a.id());
    const kb = String(b.data('canonical_id') || b.id());
    return ka < kb ? -1 : ka > kb ? 1 : (a.id() < b.id() ? -1 : a.id() > b.id() ? 1 : 0);
  });
  const cols = Math.max(1, Math.min(sorted.length, Math.floor(Math.max(bb.w, 5 * BAND_CELL_W) / BAND_CELL_W)));
  const y0 = bb.y2 + BAND_GAP;
  sorted.forEach((n, i) => {
    n.position({ x: bb.x1 + (i % cols) * BAND_CELL_W, y: y0 + Math.floor(i / cols) * BAND_CELL_H });
  });
}

function runAndSettle(cy: cytoscape.Core, target: cytoscape.Collection, cfg: any, band?: cytoscape.NodeCollection): Promise<void> {
  const split = !!band && band.nonempty();
  const flow = split ? target.not(band as cytoscape.NodeCollection).not((band as cytoscape.NodeCollection).connectedEdges()) : target;
  const layout = sortForLayout(flow).layout(cfg);
  let finish = () => {};
  let armSettleTimer = () => {};
  const done = new Promise<void>((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      layout.off('layoutstop', finish);
      if (split && !cy.destroyed() && inflight.get(cy) === done) placeContextBand(flow, band as cytoscape.NodeCollection);
      resolve();
    };
    layout.one('layoutstop', finish);
    // Geometry preparation is synchronous. Give the worker its full settlement
    // window after setup, so an overdue watchdog cannot cancel its fallback.
    armSettleTimer = () => {
      if (settled) return;
      timer = setTimeout(() => { layout.stop(); finish(); }, LAYOUT_SETTLE_TIMEOUT_MS);
    };
  });
  inflight.set(cy, done);
  done.then(() => { if (inflight.get(cy) === done) inflight.delete(cy); });
  try {
    layout.run();
    armSettleTimer();
  } catch {
    layout.stop();
    finish();
  }
  return done;
}

/** Run a named preset over `eles` (default: all). No camera move; resolves when positions land. */
export function runNamedLayout(cy: cytoscape.Core, preset: LayoutPreset, eles?: cytoscape.Collection): Promise<void> {
  const target = eles ?? cy.elements();
  if (target.length === 0) return Promise.resolve();
  const cfg = preset === 'organic' ? elkLayoutFor(target) : PRESET_CFG[preset];
  return runAndSettle(cy, target, cfg, preset === 'organic' ? contextBandNodes(target) : undefined);
}

/** Lay out `eles` (default: all) once with ELK. No camera move; resolves when positions land. */
export function runLayout(cy: cytoscape.Core, eles?: cytoscape.Collection): Promise<void> {
  const target = eles ?? cy.elements();
  if (target.length === 0) return Promise.resolve();
  return runAndSettle(cy, target, elkLayoutFor(target), contextBandNodes(target));
}

/** Resolves when the instance's in-flight layout (if any) has placed its nodes. */
export function layoutSettled(cy: cytoscape.Core): Promise<void> {
  return inflight.get(cy) ?? Promise.resolve();
}

/** Bloom-on-arrival — a brief sky overlay on freshly-added nodes (spec §8 node states). */
export function bloom(cy: cytoscape.Core, ids: string[]) {
  ids.forEach((id) => { const n = cy.getElementById(id); if (!n.empty()) n.addClass('cs-bloom'); });
  setTimeout(() => ids.forEach((id) => { const n = cy.getElementById(id); if (!n.empty()) n.removeClass('cs-bloom'); }), 950);
}

/** Zoom limits of the Fusion canvas. The lower bound is derived per graph (see `deriveMinZoom`). */
export const MAX_ZOOM = 2.2;
export const DEFAULT_MIN_ZOOM = 0.3;
export const FLOOR_MIN_ZOOM = 0.005;

/**
 * Lowest zoom that still lets the user see all of `eles`: the zoom at which their bounding box
 * (plus padding) fits the container, with 20% headroom, never above the default 0.3 (a small
 * graph keeps the old limit) and never below a floor. Pure; returns the default when the
 * container or the box has no size (headless, hidden tab).
 */
export function deriveMinZoom(bbW: number, bbH: number, viewW: number, viewH: number, padding = 55): number {
  if (!(viewW > 0) || !(viewH > 0) || !(bbW > 0 || bbH > 0)) return DEFAULT_MIN_ZOOM;
  const fit = Math.min(
    viewW / Math.max(1, bbW + 2 * padding),
    viewH / Math.max(1, bbH + 2 * padding),
  );
  return Math.min(DEFAULT_MIN_ZOOM, Math.max(FLOOR_MIN_ZOOM, fit * 0.8));
}

/** Set the instance's zoom bounds so that framing `eles` is reachable (the fixed 0.3 clamped Fit on big graphs). */
export function applyZoomBounds(cy: cytoscape.Core, eles?: cytoscape.Collection, padding = 55): void {
  if (cy.destroyed()) return;
  const target = eles ?? visibleEles(cy);
  let min = DEFAULT_MIN_ZOOM;
  if (target.length > 0) {
    const bb = target.boundingBox({});
    min = deriveMinZoom(bb.w, bb.h, cy.width(), cy.height(), padding);
  }
  cy.minZoom(min);
  cy.maxZoom(MAX_ZOOM);
}

/** Elements Fit frames: the visible ones, after widening the zoom bounds to reach them. */
export function fitElements(cy: cytoscape.Core): cytoscape.Collection {
  const vis = visibleEles(cy);
  applyZoomBounds(cy, vis);
  return vis;
}

function doFit(cy: cytoscape.Core, eles: cytoscape.Collection | undefined, padding: number, duration: number) {
  if (cy.destroyed()) return;
  const target = (eles ?? visibleEles(cy));
  if (target.length === 0) return;
  applyZoomBounds(cy, target, padding);
  if (duration <= 0) { cy.fit(target, padding); return; }
  cy.animate({ fit: { eles: target, padding } }, { duration, easing: 'ease-out-cubic' });
}

/**
 * Frame a set of elements (default: currently visible). One camera move. If a
 * layout is still placing nodes, the fit waits for it so it frames real positions.
 */
export function fitTo(cy: cytoscape.Core, eles?: cytoscape.Collection, padding = 55, duration = 320) {
  const pending = inflight.get(cy);
  if (pending) {
    pending.then(() => doFit(cy, eles, padding, duration));
    return;
  }
  doFit(cy, eles, padding, duration);
}
